import React from 'react';
import { Link } from 'react-router-dom';
import { 
  AcademicCapIcon, 
  ClockIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  BookOpenIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

const AcademicPage = () => {
  const eligibilityRequirements = [
    {
      title: 'Minimum GPA Requirement',
      requirement: '2.0 cumulative GPA',
      description: 'Student-athletes must maintain a minimum 2.0 GPA to remain eligible for athletic participation.',
      icon: AcademicCapIcon,
      color: 'text-blue-600'
    },
    {
      title: 'Course Load',
      requirement: 'Minimum 5 classes per semester',
      description: 'Students must be enrolled in at least 5 classes to maintain full-time status and athletic eligibility.',
      icon: BookOpenIcon,
      color: 'text-green-600'
    },
    {
      title: 'Failing Grades Policy',
      requirement: 'No more than 1 failing grade',
      description: 'Students with 2 or more failing grades are immediately ineligible until grades improve.',
      icon: ExclamationTriangleIcon,
      color: 'text-red-600'
    },
    {
      title: 'Progress Monitoring',
      requirement: 'Weekly grade checks',
      description: 'Academic progress is monitored weekly through progress reports from teachers.',
      icon: ClockIcon,
      color: 'text-purple-600'
    }
  ];

  const supportServices = [
    {
      service: 'Academic Tutoring',
      description: 'Free tutoring available in all core subjects',
      schedule: 'Monday-Thursday, 3:30-4:30 PM',
      location: 'Library Study Rooms'
    },
    {
      service: 'Study Hall',
      description: 'Supervised study time for student-athletes',
      schedule: 'Daily, 7:30-8:00 AM',
      location: 'Room 205'
    },
    {
      service: 'Academic Counseling',
      description: 'One-on-one academic planning and support',
      schedule: 'By appointment',
      location: 'Guidance Office'
    },
    {
      service: 'Peer Mentoring',
      description: 'Upper-class student-athletes mentor underclassmen',
      schedule: 'Flexible scheduling',
      location: 'Various locations'
    }
  ];

  const academicCalendar = [
    {
      period: 'First Quarter',
      dates: 'August 15 - October 20',
      keyEvents: ['Eligibility check (Sept 1)', 'Progress reports (Sept 15)', 'Quarter grades due (Oct 20)']
    },
    {
      period: 'Second Quarter',
      dates: 'October 23 - December 22',
      keyEvents: ['Eligibility check (Nov 1)', 'Progress reports (Nov 15)', 'Semester grades due (Dec 22)']
    },
    {
      period: 'Third Quarter',
      dates: 'January 8 - March 15',
      keyEvents: ['Spring eligibility check (Jan 15)', 'Progress reports (Feb 15)', 'Quarter grades due (Mar 15)']
    },
    {
      period: 'Fourth Quarter',
      dates: 'March 18 - June 5',
      keyEvents: ['Final eligibility check (Apr 1)', 'Progress reports (Apr 15)', 'Final grades due (Jun 5)']
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-primary text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="mb-4">
            <Link to="/family-resources" className="text-blue-200 hover:text-white">
              Family Resources
            </Link>
            <span className="mx-2 text-blue-200">/</span>
            <span className="text-white">Academic Requirements</span>
          </nav>
          <h1 className="text-4xl font-bold mb-4">
            Academic Requirements
          </h1>
          <p className="text-xl text-blue-100">
            Academic eligibility requirements and support resources for student-athletes.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Academic Philosophy */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Academic Excellence First
          </h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            At East End Prep, we believe that academic success is the foundation for all other achievements. 
            Our student-athletes are students first, and we are committed to supporting their academic 
            growth and success both in the classroom and on the field.
          </p>
          <p className="text-gray-600 leading-relaxed">
            We provide comprehensive academic support services to ensure that all student-athletes 
            have the resources they need to excel academically while pursuing their athletic goals.
          </p>
        </div>

        {/* Eligibility Requirements */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Eligibility Requirements
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {eligibilityRequirements.map((req, index) => {
              const IconComponent = req.icon;
              return (
                <div key={index} className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <IconComponent className={`w-8 h-8 ${req.color} mr-3`} />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {req.title}
                      </h3>
                      <p className={`text-sm font-medium ${req.color}`}>
                        {req.requirement}
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    {req.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Academic Support Services */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-center mb-6">
            <UserGroupIcon className="w-8 h-8 text-primary mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">
              Academic Support Services
            </h2>
          </div>
          <p className="text-gray-600 mb-6">
            We offer various support services to help student-athletes succeed academically:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {supportServices.map((service, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {service.service}
                </h3>
                <p className="text-gray-600 mb-4">
                  {service.description}
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center">
                    <ClockIcon className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-gray-600">
                      <strong>Schedule:</strong> {service.schedule}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-gray-600">
                      <strong>Location:</strong> {service.location}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Academic Calendar */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Academic Calendar & Key Dates
          </h2>
          <div className="space-y-6">
            {academicCalendar.map((quarter, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {quarter.period}
                  </h3>
                  <span className="text-sm text-gray-500 font-medium">
                    {quarter.dates}
                  </span>
                </div>
                <div className="space-y-2">
                  {quarter.keyEvents.map((event, eventIndex) => (
                    <div key={eventIndex} className="flex items-center">
                      <CheckCircleIcon className="w-4 h-4 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-gray-600">{event}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Important Notes */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-yellow-800 mb-3">
                Important Academic Policies
              </h3>
              <ul className="space-y-2 text-yellow-700">
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-2 h-2 bg-yellow-600 rounded-full mt-2 mr-3"></span>
                  Students who become academically ineligible are immediately suspended from all athletic activities
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-2 h-2 bg-yellow-600 rounded-full mt-2 mr-3"></span>
                  Eligibility is reviewed weekly and can be restored once academic requirements are met
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-2 h-2 bg-yellow-600 rounded-full mt-2 mr-3"></span>
                  Summer school may be required for students who fail core classes
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-2 h-2 bg-yellow-600 rounded-full mt-2 mr-3"></span>
                  Academic support services are mandatory for students on academic probation
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcademicPage;